import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { useClient, useClientSubBranch, useUpdateClientSubBranch, useDeleteClientSubBranch } from "@/features/clients/hooks/useClients";
import { useCreateClientSubBranch } from "@/features/tenders/hooks/useTenders";
import { PageLoader } from "@/components/ui/Spinner";
import { usePageTitle } from "@/hooks/usePageTitle";

export function ClientSubBranchFormPage() {
  const navigate = useNavigate();
  const { clientId, branchId, subBranchId } = useParams<{
    clientId: string;
    branchId: string;
    subBranchId?: string;
  }>();
  const isEdit = Boolean(subBranchId);

  const { data: client, isLoading: loadingClient } = useClient(clientId!);
  const { data: subBranch, isLoading: loadingSubBranch } = useClientSubBranch(subBranchId ?? "");
  usePageTitle(isEdit ? (subBranch && `Pododdział: ${subBranch.name}`) : null);

  const createSubBranch = useCreateClientSubBranch();
  const updateSubBranch = useUpdateClientSubBranch(subBranchId ?? "");
  const deleteSubBranch = useDeleteClientSubBranch();
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteInput, setDeleteInput] = useState("");

  const [form, setForm] = useState({
    name: "",
    postal_code: "",
    city: "",
    contact_person: "",
    contact_email: "",
    contact_phone: "",
    notes: "",
    is_active: true,
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (subBranch) {
      setForm({
        name: subBranch.name,
        postal_code: subBranch.postal_code,
        city: subBranch.city,
        contact_person: subBranch.contact_person,
        contact_email: subBranch.contact_email,
        contact_phone: subBranch.contact_phone,
        notes: subBranch.notes,
        is_active: subBranch.is_active,
      });
    }
  }, [subBranch]);

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = "Nazwa jest wymagana";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setFieldErrors({});
    try {
      if (isEdit) {
        await updateSubBranch.mutateAsync({
          name: form.name.trim(),
          postal_code: form.postal_code.trim() || undefined,
          city: form.city.trim() || undefined,
          contact_person: form.contact_person.trim() || undefined,
          contact_email: form.contact_email.trim() || undefined,
          contact_phone: form.contact_phone.trim() || undefined,
          notes: form.notes.trim() || undefined,
          is_active: form.is_active,
        });
      } else {
        await createSubBranch.mutateAsync({
          branch: branchId!,
          name: form.name.trim(),
          postal_code: form.postal_code.trim() || undefined,
          city: form.city.trim() || undefined,
          contact_person: form.contact_person.trim() || undefined,
          contact_email: form.contact_email.trim() || undefined,
          contact_phone: form.contact_phone.trim() || undefined,
          notes: form.notes.trim() || undefined,
        });
      }
      navigate(`/clients/${clientId}`);
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { errors?: Record<string, string[]> } } })
        ?.response?.data?.errors;
      if (data) {
        const parsed: Record<string, string> = {};
        for (const [k, v] of Object.entries(data)) {
          parsed[k] = Array.isArray(v) ? v[0] : String(v);
        }
        setFieldErrors(parsed);
      }
    }
  };

  if (loadingClient || (isEdit && loadingSubBranch)) return <PageLoader />;

  const isPending = createSubBranch.isPending || updateSubBranch.isPending;
  const branchDisplayName = subBranch?.branch_name ?? `Oddział`;

  return (
    <div className="max-w-xl">
      <Link
        to={`/clients/${clientId}`}
        className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 mb-5"
      >
        <ArrowLeft size={14} /> {client?.name ?? "Klient"}
      </Link>

      <div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
          {branchDisplayName}
        </p>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {isEdit ? "Edytuj pododdział" : "Nowy pododdział"}
        </h1>
        <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-6" />
      </div>

      <div className="bg-white dark:bg-gray-800 p-6 flex flex-col gap-4 mb-6">
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Nazwa <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className={`block w-full rounded-md border px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${fieldErrors.name ? "border-red-400" : "border-gray-300 dark:border-gray-600"}`}
            placeholder="np. Pododdział Centrum"
          />
          {fieldErrors.name && <p className="text-xs text-red-600 mt-0.5">{fieldErrors.name}</p>}
        </div>

        <div className="grid grid-cols-[8rem_1fr] gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Kod pocztowy</label>
            <input
              type="text"
              value={form.postal_code}
              onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="00-000"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Miasto</label>
            <input
              type="text"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="np. Wrocław"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Osoba kontaktowa</label>
            <input
              type="text"
              value={form.contact_person}
              onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Jan Kowalski"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Telefon</label>
            <input
              type="text"
              value={form.contact_phone}
              onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="+48 123 456 789"
            />
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">E-mail</label>
          <input
            type="email"
            value={form.contact_email}
            onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
            className="block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            placeholder="pododdział@firma.pl"
          />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Uwagi</label>
          <textarea
            rows={3}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            placeholder="Opcjonalne notatki"
          />
        </div>

        {isEdit && (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Aktywny</span>
          </label>
        )}
      </div>

      {fieldErrors.detail && (
        <p className="text-sm text-red-600 mb-4">{fieldErrors.detail}</p>
      )}

      <div className="flex gap-3">
        <Button loading={isPending} onClick={handleSubmit}>
          {isEdit ? "Zapisz zmiany" : "Utwórz pododdział"}
        </Button>
        <Button variant="secondary" onClick={() => navigate(`/clients/${clientId}`)}>
          Anuluj
        </Button>
      </div>

      {isEdit && (
        <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={() => { setDeleteConfirm(true); setDeleteInput(""); }}
            className="text-xs text-gray-400 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-500 transition-colors underline underline-offset-2"
          >
            Usuń pododdział
          </button>
        </div>
      )}

      {deleteConfirm && createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6 flex flex-col gap-4">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Usuń pododdział</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Pododdział zostanie oznaczony jako <strong>nieaktywny</strong>. Powiązane przedsięwzięcia pozostaną w systemie.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Aby potwierdzić, wpisz:{" "}
              <strong className="text-gray-900 dark:text-gray-100 font-mono">usuń pododdział</strong>
            </p>
            <input
              autoFocus
              value={deleteInput}
              onChange={(e) => setDeleteInput(e.target.value)}
              placeholder="usuń pododdział"
              className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteConfirm(false)}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg"
              >
                Anuluj
              </button>
              <button
                onClick={() => deleteSubBranch.mutate(subBranchId!, { onSuccess: () => navigate(`/clients/${clientId}`) })}
                disabled={deleteInput !== "usuń pododdział" || deleteSubBranch.isPending}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {deleteSubBranch.isPending ? "Usuwanie…" : "Usuń pododdział"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
